// src/lib/api.ts — the single HTTP client. Handles the response envelope, bearer
// tokens and transparent refresh-on-401.

import type { ApiError, ListMeta } from '../types/api';

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

export class ApiRequestError extends Error {
  code: string;
  status: number;
  details?: unknown;
  constructor(status: number, err: ApiError) {
    super(err.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = err.code;
    this.details = err.details;
  }
}

type Tokens = { accessToken: string; refreshToken: string };

const STORAGE_KEY = 'lotus.auth';

export const tokenStore = {
  get(): Tokens | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Tokens) : null;
    } catch {
      return null;
    }
  },
  set(tokens: Tokens) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens)); } catch { /* private mode */ }
  },
  clear() {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* private mode */ }
  },
};

let refreshing: Promise<boolean> | null = null;

/** Exchange the refresh token for a new pair. Deduplicated across concurrent 401s. */
async function refreshTokens(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const tokens = tokenStore.get();
    if (!tokens?.refreshToken) return false;
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken: tokens.refreshToken }),
      });
      if (!res.ok) return false;
      const body = await res.json();
      const next = body?.data;
      if (!next?.accessToken) return false;
      tokenStore.set({ accessToken: next.accessToken, refreshToken: next.refreshToken ?? tokens.refreshToken });
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the Authorization header (public endpoints). */
  anonymous?: boolean;
  query?: Record<string, unknown>;
}

function buildUrl(path: string, query?: Record<string, unknown>): string {
  const url = `${BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function request<T>(path: string, options: RequestOptions = {}, retry = true): Promise<{ data: T; meta?: ListMeta }> {
  const { body, anonymous, query, headers, ...rest } = options;
  const tokens = tokenStore.get();

  const res = await fetch(buildUrl(path, query), {
    ...rest,
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(!anonymous && tokens?.accessToken ? { authorization: `Bearer ${tokens.accessToken}` } : {}),
      ...(headers as Record<string, string>),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && retry && !anonymous) {
    if (await refreshTokens()) return request<T>(path, options, false);
    tokenStore.clear();
  }

  if (res.status === 204) return { data: undefined as T };

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    throw new ApiRequestError(res.status, payload?.error ?? { code: 'NETWORK', message: res.statusText });
  }
  return { data: payload?.data as T, meta: payload?.meta };
}

export const api = {
  get: <T>(path: string, query?: Record<string, unknown>) => request<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Unauthenticated calls for the public website surface. */
  public: {
    get: <T>(path: string, query?: Record<string, unknown>) =>
      request<T>(path, { method: 'GET', query, anonymous: true }),
    post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body, anonymous: true }),
  },
};

/**
 * Open an SSE stream against a POST endpoint. EventSource cannot POST or send
 * headers, so this reads the response body directly.
 */
export async function streamSse(
  path: string,
  body: unknown,
  handlers: { onEvent: (event: string, data: unknown) => void; signal?: AbortSignal; anonymous?: boolean },
): Promise<void> {
  const tokens = tokenStore.get();
  const res = await fetch(buildUrl(path), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(!handlers.anonymous && tokens?.accessToken ? { authorization: `Bearer ${tokens.accessToken}` } : {}),
    },
    body: JSON.stringify(body),
    signal: handlers.signal,
  });

  if (!res.ok || !res.body) {
    const payload = await res.json().catch(() => null);
    throw new ApiRequestError(res.status, payload?.error ?? { code: 'STREAM_FAILED', message: res.statusText });
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line.
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';
    for (const frame of frames) {
      let event = 'message';
      const dataLines: string[] = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
      }
      if (!dataLines.length) continue;
      try {
        handlers.onEvent(event, JSON.parse(dataLines.join('\n')));
      } catch {
        handlers.onEvent(event, dataLines.join('\n'));
      }
    }
  }
}
